import React, { useState, useMemo, useEffect } from 'react';
import { Search, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, X } from 'lucide-react';

// Tabla con las funciones de DataTables (búsqueda global, filtro por
// columna, orden, tamaño de página y paginación) hecha en React: las filas
// siguen siendo de React, así que los onClick y botones por fila funcionan
// sin tocar el DOM por fuera.
//
// columnas: [{ id, titulo, render(fila), valor(fila) para ordenar/filtrar,
//              ordenable, filtro: [{ valor, etiqueta }], alinear: 'center'|'right' }]

const TAMANOS = [10, 25, 50, 100];

const normalizar = (v) => String(v ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Páginas a mostrar: siempre la primera y la última, y dos alrededor de la actual.
const paginasVisibles = (actual, total) => {
  const set = new Set([1, total, actual - 1, actual, actual + 1].filter(p => p >= 1 && p <= total));
  const orden = [...set].sort((a, b) => a - b);
  const resultado = [];
  orden.forEach((p, i) => {
    if (i > 0 && p - orden[i - 1] > 1) resultado.push(`hueco-${p}`);
    resultado.push(p);
  });
  return resultado;
};

const TablaDatos = ({
  columnas,
  filas,
  clave,
  textoBusqueda,
  onFilaClick,
  filaActiva,
  vacio = 'Sin registros',
  placeholderBusqueda = 'Buscar…',
  tamanoInicial = 10
}) => {
  const [busqueda, setBusqueda] = useState('');
  const [filtros, setFiltros] = useState({});
  const [orden, setOrden] = useState({ id: null, dir: 'asc' });
  const [tamano, setTamano] = useState(tamanoInicial);
  const [pagina, setPagina] = useState(1);

  const columnasConFiltro = columnas.filter(c => c.filtro);
  const hayFiltros = busqueda.trim() !== '' || Object.values(filtros).some(v => v !== '');

  const procesadas = useMemo(() => {
    const q = normalizar(busqueda.trim());
    let resultado = filas.filter(f => {
      if (q && !normalizar(textoBusqueda(f)).includes(q)) return false;
      return columnas.filter(c => c.filtro).every(c => {
        const elegido = filtros[c.id];
        return !elegido || String(c.valor(f)) === elegido;
      });
    });

    const col = columnas.find(c => c.id === orden.id);
    if (col) {
      const signo = orden.dir === 'asc' ? 1 : -1;
      resultado = [...resultado].sort((a, b) => {
        const va = col.valor(a);
        const vb = col.valor(b);
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * signo;
        return String(va ?? '').localeCompare(String(vb ?? ''), 'es', { numeric: true, sensitivity: 'base' }) * signo;
      });
    }
    return resultado;
  }, [filas, busqueda, filtros, orden, columnas, textoBusqueda]);

  const totalPaginas = Math.max(1, Math.ceil(procesadas.length / tamano));

  // Al cambiar búsqueda, filtros o tamaño se vuelve a la primera página.
  useEffect(() => { setPagina(1); }, [busqueda, filtros, tamano]);
  useEffect(() => { if (pagina > totalPaginas) setPagina(totalPaginas); }, [pagina, totalPaginas]);

  const inicio = (pagina - 1) * tamano;
  const visibles = procesadas.slice(inicio, inicio + tamano);

  const alternarOrden = (id) => setOrden(prev =>
    prev.id !== id ? { id, dir: 'asc' } : prev.dir === 'asc' ? { id, dir: 'desc' } : { id: null, dir: 'asc' }
  );

  const limpiar = () => { setBusqueda(''); setFiltros({}); };

  const alinear = (c) => (c.alinear === 'center' ? 'text-center' : c.alinear === 'right' ? 'text-right' : 'text-left');

  return (
    <div>
      <div className="px-4 py-3 flex flex-col lg:flex-row lg:items-center gap-3 border-b border-gray-100">
        <label className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
          Mostrar
          <select
            value={tamano}
            onChange={(e) => setTamano(Number(e.target.value))}
            className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          >
            {TAMANOS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          registros
        </label>

        <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
          {columnasConFiltro.map(c => (
            <select
              key={c.id}
              value={filtros[c.id] || ''}
              onChange={(e) => setFiltros(prev => ({ ...prev, [c.id]: e.target.value }))}
              aria-label={`Filtrar por ${c.titulo}`}
              className={`px-2 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none ${
                filtros[c.id] ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-600'
              }`}
            >
              <option value="">{c.titulo}: todos</option>
              {c.filtro.map(op => <option key={op.valor} value={op.valor}>{op.etiqueta}</option>)}
            </select>
          ))}
          <div className="relative flex-1 min-w-[12rem] lg:w-64 lg:flex-none">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder={placeholderBusqueda}
              className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          {hayFiltros && (
            <button
              type="button"
              onClick={limpiar}
              className="px-2 py-1.5 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <X size={14} /> Limpiar
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500">
              {columnas.map(c => (
                <th
                  key={c.id}
                  className={`px-4 py-2 font-medium ${alinear(c)}`}
                  aria-sort={orden.id === c.id ? (orden.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {c.ordenable ? (
                    <button
                      type="button"
                      onClick={() => alternarOrden(c.id)}
                      className={`inline-flex items-center gap-1 hover:text-gray-800 outline-none focus-visible:text-blue-600 ${
                        orden.id === c.id ? 'text-gray-800' : ''
                      }`}
                    >
                      {c.titulo}
                      {orden.id !== c.id
                        ? <ChevronsUpDown size={12} className="text-gray-300" />
                        : orden.dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                  ) : c.titulo}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibles.length === 0 ? (
              <tr>
                <td colSpan={columnas.length} className="px-4 py-8 text-center text-gray-400">
                  {hayFiltros ? 'Ningún registro coincide con la búsqueda o los filtros' : vacio}
                </td>
              </tr>
            ) : visibles.map(f => {
              const activa = filaActiva?.(f);
              return (
                <tr
                  key={clave(f)}
                  onClick={onFilaClick ? () => onFilaClick(f) : undefined}
                  tabIndex={onFilaClick ? 0 : undefined}
                  onKeyDown={onFilaClick ? (e) => {
                    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      onFilaClick(f);
                    }
                  } : undefined}
                  className={`border-b border-gray-100 ${onFilaClick ? 'cursor-pointer' : ''} ${
                    activa ? 'bg-blue-50' : onFilaClick ? 'hover:bg-gray-50' : ''
                  }`}
                >
                  {columnas.map(c => (
                    <td key={c.id} className={`px-4 py-2 ${alinear(c)}`}>
                      {c.render ? c.render(f) : c.valor(f)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 text-sm text-gray-600 border-t border-gray-100">
        <span>
          {procesadas.length === 0
            ? 'Sin registros'
            : `Mostrando ${inicio + 1}–${Math.min(inicio + tamano, procesadas.length)} de ${procesadas.length}`}
          {procesadas.length !== filas.length && (
            <span className="text-gray-400"> (filtrado de {filas.length})</span>
          )}
        </span>

        {totalPaginas > 1 && (
          <nav className="sm:ml-auto flex items-center gap-1" aria-label="Paginación">
            <button
              type="button"
              onClick={() => setPagina(p => p - 1)}
              disabled={pagina === 1}
              aria-label="Página anterior"
              className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <ChevronLeft size={16} />
            </button>
            {paginasVisibles(pagina, totalPaginas).map(p => (
              typeof p === 'string' ? (
                <span key={p} className="px-1 text-gray-400">…</span>
              ) : (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPagina(p)}
                  aria-current={p === pagina ? 'page' : undefined}
                  className={`min-w-[2rem] h-8 px-2 rounded-lg tabular-nums ${
                    p === pagina ? 'bg-blue-600 text-white font-medium' : 'hover:bg-gray-100'
                  }`}
                >
                  {p}
                </button>
              )
            ))}
            <button
              type="button"
              onClick={() => setPagina(p => p + 1)}
              disabled={pagina === totalPaginas}
              aria-label="Página siguiente"
              className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <ChevronRight size={16} />
            </button>
          </nav>
        )}
      </div>
    </div>
  );
};

export default TablaDatos;
